<template>
  <div id="kdm-payment-page">
    <!-- Hata Bildirimi -->
    <vs-alert color="danger" title="Hata" :active.sync="hasError">
      <span>{{ errorMessage }}</span>
    </vs-alert>

    <!-- Yükleniyor -->
    <div v-if="loading" class="flex justify-center items-center" style="min-height: 400px;">
      <vs-progress indeterminate color="primary"></vs-progress>
    </div>

    <!-- Ana İçerik -->
    <div v-if="!loading && !hasError && invoiceData && kdmData" class="vx-row">
      <!-- SOL KOLON: Kart Formu -->
      <div class="vx-col lg:w-2/3 w-full">
        <!-- KDM Fatura Bilgileri -->
        <vx-card title="Fatura Bilgileri" class="mb-base">
          <div class="vx-row">
            <div class="vx-col w-full md:w-1/2 mb-3">
              <label class="text-sm opacity-75">Ad Soyad</label>
              <p class="font-semibold">{{ kdmData.KisiAdSoyad }}</p>
            </div>
            <div class="vx-col w-full md:w-1/2 mb-3">
              <label class="text-sm opacity-75">TC Kimlik No</label>
              <p class="font-semibold">{{ kdmData.KisiTcNo }}</p>
            </div>
            <div class="vx-col w-full md:w-1/2 mb-3">
              <label class="text-sm opacity-75">E-Posta</label>
              <p class="font-semibold">{{ kdmData.KisiMail }}</p>
            </div>
            <div class="vx-col w-full md:w-1/2 mb-3">
              <label class="text-sm opacity-75">Hizmet Kodu</label>
              <p class="font-semibold">{{ kdmData.HizmetKodu }}</p>
            </div>
            <div class="vx-col w-full mb-3" v-if="kdmData.Aciklama">
              <label class="text-sm opacity-75">Açıklama</label>
              <p class="font-semibold">{{ kdmData.Aciklama }}</p>
            </div>
            <div class="vx-col w-full mb-3" v-if="kdmData.KisiAdres">
              <label class="text-sm opacity-75">Adres</label>
              <p class="font-semibold">{{ kdmData.KisiAdres }}</p>
            </div>
          </div>
        </vx-card>

        <!-- Kredi Kartı Formu -->
        <vx-card title="Kredi Kartı Bilgileri" subtitle="Lütfen kart bilgilerinizi giriniz." class="mb-base">
          <div class="vx-row mt-3">
            <div class="vx-col w-full md:w-1/2 sm:w-1/1 mb-base">
              <ValidationObserver tag="form" ref="paymentForm" v-on:submit.prevent v-slot="{ invalid }">
                <ValidationProvider name="Kart Sahibi" rules="required|min:3" v-slot="{ errors }">
                  <vs-input icon="icon icon-user" icon-pack="feather" label-placeholder="Kart Sahibi"
                    type="text" v-letter-only @input="changeName" data-card-field
                    autocomplete="off" class="w-full no-icon-border card-input__input" v-model="formData.cardName" />
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>

                <ValidationProvider name="Kart Numarası" rules="required|ccnumber" v-slot="{ errors }">
                  <vs-input type="tel" icon="icon icon-credit-card" icon-pack="feather"
                    label-placeholder="Kart Numarası" @input="changeNumber"
                    @focus="focusCardNumber" @blur="blurCardNumber" data-card-field autocomplete="off"
                    :maxlength="cardNumberMaxLength" class="w-full no-icon-border card-input__input"
                    v-model="formData.cardNumber" />
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>

                <div class="vx-row mt-5">
                  <div class="vx-col w-full md:w-1/3 sm:w-1/3 md:mb-base">
                    <label class="text-sm opacity-75">Ay</label>
                    <v-select :options="cardMonthOptions" v-model="formData.cardMonth"
                      :clearable="false" :dir="$vs.rtl ? 'rtl' : 'ltr'" class="mb-4 md:mb-0" @change="changeMonth"
                      data-card-field />
                  </div>
                  <div class="vx-col w-full md:w-1/3 sm:w-1/3 md:mb-base">
                    <label class="text-sm opacity-75">Yıl</label>
                    <v-select :options="cardYearOptions" v-model="formData.cardYear"
                      :clearable="false" :dir="$vs.rtl ? 'rtl' : 'ltr'" class="mb-4 md:mb-0" @change="changeYear"
                      data-card-field />
                  </div>
                  <div class="vx-col w-full md:w-1/5 sm:w-1/5 md:mb-base">
                    <ValidationProvider name="CVV" rules="required|min:3|max:4" v-slot="{ errors }">
                      <vs-input type="tel" label-placeholder="CVV" @input="changeCvv"
                        data-card-field autocomplete="off" v-number-only
                        class="w-full no-icon-border card-input__input mt-6" v-model="formData.cardCvv" />
                      <span class="text-danger text-sm">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </div>
                </div>
              </ValidationObserver>
            </div>
            <div class="vx-col w-full md:w-1/2 sm:w-1/1 mb-base">
              <Card :fields="fields" :labels="formData" :isCardNumberMasked="isCardNumberMasked"
                :randomBackgrounds="false" />
            </div>
          </div>
        </vx-card>
      </div>

      <!-- SAĞ KOLON: Ödeme Özeti + Taksit + Buton -->
      <div class="vx-col lg:w-1/3 w-full">
        <vx-card title="Ödeme Özeti" class="mb-base">
          <div class="flex justify-between mb-2">
            <span>{{ kdmData.Aciklama || 'KDM Ödeme' }}</span>
            <span class="font-semibold">{{ kdmData.Tutar | currency }}</span>
          </div>

          <div class="flex justify-between mb-2" v-if="payFormData.Installment > 1 && selectedInstallmentData.Index">
            <span>{{ payFormData.Installment }} Taksit Toplam</span>
            <span class="text-success">{{ selectedInstallmentData.TotalPrice | currency }}</span>
          </div>

          <vs-divider />

          <div class="flex justify-between">
            <span class="font-bold text-lg">Ödenecek Tutar</span>
            <span class="font-bold text-lg text-primary">{{ cartPrice | currency }}</span>
          </div>
        </vx-card>

        <!-- Taksit Seçenekleri -->
        <vx-card title="Taksit Seçenekleri" class="mb-base">
          <div class="mt-3">
            <img
              v-if="invoiceInstallments && invoiceInstallments.Installments && invoiceInstallments.Installments.length > 0 && invoiceInstallments.Logo"
              :src="invoiceInstallments.Logo" alt="bank-logo" class="float-right inline-flex brand-image" />

            <ul>
              <li class="mb-3">
                <div class="flex flex-wrap justify-between items-center">
                  <vs-radio vs-value="1" v-model="payFormData.Installment">
                    <div class="flex items-center">
                      <span class="ml-5">
                        Tek Çekim :
                        <b>{{ kdmData.Tutar | currency }}</b>
                      </span>
                    </div>
                  </vs-radio>
                </div>
              </li>
              <li class="mb-3" v-for="installement in invoiceInstallments.Installments" :key="installement.Index">
                <div class="flex flex-wrap justify-between items-center">
                  <vs-radio :vs-value="installement.Index" v-model="payFormData.Installment">
                    <div class="flex items-center">
                      <span class="ml-5">
                        {{ installement.Index }} Taksit
                        <b>{{ installement.MonthlyPrice | currency }}</b>
                        Toplam:
                        <b>{{ installement.TotalPrice | currency }}</b>
                      </span>
                    </div>
                  </vs-radio>
                </div>
              </li>
            </ul>
          </div>
        </vx-card>

        <br />

        <vs-button class="w-full paymentSubmit vs-con-loading__container" size="large"
          @click="doPayment()" color="success" type="filled" icon-pack="feather" icon="icon-shopping-cart">
          Ödemeyi Tamamla
        </vs-button>
      </div>
    </div>

    <!-- 3D Secure Yönlendirme Formları -->
    <form ref="threeDRedirectForm" method="post" :action="threeDInfo.AcsUrl">
      <input type="hidden" name="PaReq" :value="threeDInfo.PAReq" />
      <input type="hidden" name="TermUrl" :value="threeDInfo.TermUrl" />
      <input type="hidden" name="MD" :value="threeDInfo.MD" />
    </form>
    <form ref="threeDRedirectFormHidden" method="post" :action="threeDInfo.AcsUrl">
      <input v-for="(val, key) in threeDInfo.Model" :key="key" type="hidden" :name="key" :value="val" />
    </form>

    <!-- Ödeme Sonucu -->
    <div v-if="showTransaction" class="vx-row mt-5">
      <div class="vx-col w-full">
        <vs-alert :color="transactionSuccess ? 'success' : 'danger'" title="Ödeme Sonucu" :active="true">
          <span v-if="transactionSuccess">
            Ödemeniz başarıyla tamamlandı. KDM bildirimi gönderiliyor...
          </span>
          <span v-else>
            Ödeme işlemi başarısız oldu. Lütfen tekrar deneyiniz.
            <br /><br />
            <a @click="showTransaction = false; submitBusy = false;" class="text-inherit underline cursor-pointer">
              Tekrar Dene
            </a>
          </span>
        </vs-alert>

        <vs-alert v-if="callbackResult" :color="callbackResult.success ? 'success' : 'warning'"
          title="KDM Bildirimi" :active="true" class="mt-3">
          <div class="flex items-center">
            <vs-chip :color="callbackResult.success ? 'success' : 'danger'" class="mr-2">
              {{ callbackResult.success ? 'GÖNDERİLDİ' : 'HATA' }}
            </vs-chip>
            <span>{{ callbackResult.message }}</span>
          </div>
        </vs-alert>
      </div>
    </div>
  </div>
</template>

<script>
import InvoiceApi from "@/api/invoice";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import Card from "@/components/Card.vue";

var cardValid = require("card-validator");

export default {
  name: "KdmPayment",
  components: {
    ValidationProvider,
    ValidationObserver,
    Card,
    vSelect
  },
  data() {
    return {
      loading: true,
      hasError: false,
      errorMessage: "",
      // KDM verileri
      kdmData: null,
      invoiceKey: "",
      // Invoice verileri
      invoiceData: null,
      // Kart formu
      formData: {
        cardName: "",
        cardNumber: "",
        cardMonth: { label: "Ay", value: "" },
        cardYear: { label: "Yıl", value: "" },
        cardCvv: ""
      },
      payFormData: {
        Paymethod: "VakifbankClassic",
        InvoiceKey: "",
        CardOwner: "",
        CardNumber: "",
        CardMonth: "",
        CardYear: "",
        CardCvv: "",
        Installment: 1
      },
      fields: {
        cardNumber: "v-card-number",
        cardName: "v-card-name",
        cardMonth: "v-card-month",
        cardYear: "v-card-year",
        cardCvv: "v-card-cvv"
      },
      isCardNumberMasked: true,
      mainCardNumber: "",
      cardNumberMaxLength: 19,
      // Taksit
      invoiceInstallments: [],
      selectedInstallmentData: {},
      checkBinAvailable: true,
      lastBinCN: "",
      binBusy: false,
      cartPrice: 0,
      // 3D Secure
      threeDInfo: {
        AcsUrl: "",
        PAReq: "",
        TermUrl: "",
        MD: "",
        Model: {}
      },
      // İşlem sonucu
      showTransaction: false,
      transactionSuccess: false,
      Transaction: {},
      submitBusy: false,
      callbackResult: null,
      // Ay/Yıl seçenekleri
      cardMonthOptions: [
        { label: "Ay", value: "" },
        { label: "1", value: "01" },
        { label: "2", value: "02" },
        { label: "3", value: "03" },
        { label: "4", value: "04" },
        { label: "5", value: "05" },
        { label: "6", value: "06" },
        { label: "7", value: "07" },
        { label: "8", value: "08" },
        { label: "9", value: "09" },
        { label: "10", value: "10" },
        { label: "11", value: "11" },
        { label: "12", value: "12" }
      ]
    };
  },
  computed: {
    cardYearOptions() {
      const currentYear = new Date().getFullYear();
      let options = [{ label: "Yıl", value: "" }];
      for (let i = 0; i < 12; i++) {
        options.push({ label: String(currentYear + i), value: String(currentYear + i) });
      }
      return options;
    }
  },
  directives: {
    "number-only": {
      bind(el) {
        function checkValue(event) {
          event.target.value = event.target.value.replace(/[^0-9]/g, "");
          if (event.charCode >= 48 && event.charCode <= 57) return true;
          event.preventDefault();
        }
        el.addEventListener("keypress", checkValue);
      }
    },
    "letter-only": {
      bind(el) {
        function checkValue(event) {
          if (event.charCode >= 48 && event.charCode <= 57) event.preventDefault();
          return true;
        }
        el.addEventListener("keypress", checkValue);
      }
    }
  },
  watch: {
    "payFormData.Installment"() {
      if (this.payFormData.Installment <= 1) {
        this.cartPrice = this.kdmData.Tutar || 0;
        this.selectedInstallmentData = {};
      } else if (this.invoiceInstallments && this.invoiceInstallments.Installments) {
        var selected = this.invoiceInstallments.Installments.find(
          x => x.Index == this.payFormData.Installment
        );
        if (selected) {
          this.cartPrice = selected.TotalPrice;
          this.selectedInstallmentData = selected;
        }
      }
    }
  },
  methods: {
    async initPage() {
      this.loading = true;
      const hashData = this.$route.params.hashData;

      try {
        // 1. Backend'e hashData gönder → Invoice oluştur + KDM verileri al
        var result = await InvoiceApi.prepareKdmInvoice({ HashData: hashData });

        if (result.data.error) {
          this.errorMessage = result.data.error;
          this.hasError = true;
          this.loading = false;
          return;
        }

        this.invoiceKey = result.data.redirect;
        this.kdmData = result.data.paymentData;
        this.cartPrice = this.kdmData.Tutar;
        this.payFormData.InvoiceKey = this.invoiceKey;

        // 2. Invoice verilerini çek (taksit bilgileri vb. için)
        var invoiceResult = await InvoiceApi.getInvoice({ InvoiceKey: this.invoiceKey }, this);

        if (!invoiceResult.data.error) {
          this.invoiceData = invoiceResult.data;

          // TransactionId kontrolü (3D Secure dönüşü)
          const transactionId = this.$route.params.transactionId;
          if (transactionId && transactionId > 0) {
            var checkTransaction = this.invoiceData.Invoice.Transactions.find(
              x => x.TransactionID == transactionId
            );
            if (checkTransaction) {
              this.showTransaction = true;
              this.Transaction = checkTransaction;
              this.transactionSuccess = checkTransaction.TransactionState == 0;

              // Ödeme başarılıysa KDM callback gönder
              if (this.transactionSuccess) {
                await this.sendCallback();
              }
            }
          }
        } else {
          this.errorMessage = invoiceResult.data.error || "Fatura bilgileri alınamadı.";
          this.hasError = true;
        }
      } catch (e) {
        this.errorMessage = "Bağlantı hatası. Lütfen tekrar deneyiniz.";
        this.hasError = true;
      }

      this.loading = false;
    },

    // KDM'e ödeme bildirimi gönder
    async sendCallback() {
      try {
        var callbackResult = await InvoiceApi.sendKdmCallback({
          ApplicationId: this.kdmData.ApplicationId,
          InvoiceKey: this.invoiceKey
        });
        this.callbackResult = callbackResult.data;
      } catch (e) {
        this.callbackResult = { success: false, message: "Callback gönderilemedi." };
      }
    },

    // Ödeme işlemini başlat
    async doPayment() {
      const isValid = await this.$refs.paymentForm.validate();
      if (this.submitBusy || !isValid) return;

      // Kart numarası validasyonu
      var numberValidation = cardValid.number(this.payFormData.CardNumber);
      if (!numberValidation.isPotentiallyValid) {
        this.$vs.notify({
          time: 2500, title: "Uyarı", text: "Geçersiz kart numarası.",
          iconPack: "feather", icon: "icon-alert-circle", color: "danger", position: "top-right"
        });
        return;
      }

      // Son kullanma tarihi validasyonu
      if (this.formData.cardMonth.value && this.formData.cardYear.value) {
        var expiryValidation = cardValid.expirationDate({
          month: this.formData.cardMonth.value,
          year: this.formData.cardYear.value
        });
        if (!expiryValidation.isPotentiallyValid) {
          this.$vs.notify({
            time: 2500, title: "Uyarı", text: "Geçersiz son kullanma tarihi.",
            iconPack: "feather", icon: "icon-alert-circle", color: "danger", position: "top-right"
          });
          return;
        }
      }

      this.submitBusy = true;
      this.$vs.loading({ color: "#000", container: ".paymentSubmit", scale: 0.45 });

      try {
        this.payFormData.InvoiceKey = this.invoiceKey;
        this.payFormData.CardOwner = this.formData.cardName;
        this.payFormData.CardMonth = this.formData.cardMonth.value.toString();
        this.payFormData.CardYear = this.formData.cardYear.value.toString();
        this.payFormData.Installment = this.payFormData.Installment.toString();

        let payResponse = await InvoiceApi.doPayment(this.payFormData, this);

        if (!payResponse.data.error && !payResponse.data.errors) {
          if (!payResponse.data.HiddenForm) {
            this.threeDInfo.AcsUrl = payResponse.data.AcsUrl;
            this.threeDInfo.PAReq = payResponse.data.PAReq;
            this.threeDInfo.TermUrl = payResponse.data.TermUrl;
            this.threeDInfo.MD = payResponse.data.MD;

            setTimeout(() => {
              this.$refs.threeDRedirectForm.submit();
            }, 100);
          } else {
            this.threeDInfo.AcsUrl = payResponse.data.AcsUrl;
            this.threeDInfo.Model = payResponse.data.Model;

            setTimeout(() => {
              this.$refs.threeDRedirectFormHidden.submit();
            }, 100);
          }
        }
      } finally {
        this.submitBusy = false;
      }
    },

    // Kart input handler'lar
    changeName(e) {
      this.$emit("input-card-name", this.formData.cardName);
    },
    async changeNumber(e) {
      let value = this.formData.cardNumber.replace(/\D/g, "");
      this.payFormData.CardNumber = value;

      if (/^3[47]\d{0,13}$/.test(value)) {
        this.formData.cardNumber = value.replace(/(\d{4})/, "$1 ").replace(/(\d{4}) (\d{6})/, "$1 $2 ");
        this.cardNumberMaxLength = 17;
      } else if (/^3(?:0[0-5]|[68]\d)\d{0,11}$/.test(value)) {
        this.formData.cardNumber = value.replace(/(\d{4})/, "$1 ").replace(/(\d{4}) (\d{6})/, "$1 $2 ");
        this.cardNumberMaxLength = 16;
      } else if (/^\d{0,16}$/.test(value)) {
        this.formData.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{4})/, "$1 $2 ")
          .replace(/(\d{4}) (\d{4}) (\d{4})/, "$1 $2 $3 ");
        this.cardNumberMaxLength = 19;
      }

      // BIN kontrolü (taksit seçenekleri)
      if (value.length >= 6 && this.lastBinCN != value.substring(0, 6)) {
        this.invoiceInstallments = [];
        this.checkBinAvailable = true;
      }

      if (this.checkBinAvailable && value.length >= 6) {
        this.checkBinAvailable = false;
        this.binBusy = true;
        let binResult = await InvoiceApi.getBin({
          InvoiceKey: this.invoiceKey,
          cardNumber: value
        });

        if (!binResult.data.error) {
          if (binResult.data.Installments)
            this.invoiceInstallments = binResult.data;
        }

        this.lastBinCN = value.substring(0, 6);
        this.binBusy = false;
      } else if (value.length < 6) {
        this.checkBinAvailable = true;
        this.invoiceInstallments = [];
        this.lastBinCN = "";
        this.payFormData.Installment = 1;
      }
    },
    changeMonth() {
      this.payFormData.CardMonth = this.formData.cardMonth;
    },
    changeYear() {
      this.payFormData.CardYear = this.formData.cardYear;
    },
    changeCvv(e) {
      this.payFormData.CardCvv = this.formData.cardCvv;
    },
    blurCardNumber() {
      if (this.isCardNumberMasked) this.maskCardNumber();
    },
    maskCardNumber() {
      return (this.mainCardNumber = this.formData.cardNumber);
    },
    unMaskCardNumber() {
      this.formData.cardNumber = this.mainCardNumber;
    },
    focusCardNumber() {
      this.unMaskCardNumber();
    }
  },
  async mounted() {
    await this.initPage();
    this.maskCardNumber();
  }
};
</script>

<style lang="scss">
.wizard-navigation {
  margin-bottom: 12%;
}
</style>

<style lang="scss" scoped>
.vs-input--placeholder {
  color: black;
  font-weight: 600;
}

#kdm-payment-page {
  .brand-image {
    width: 150px;
    object-fit: cover;
  }
}
</style>
